// UserAccessOverview — read-only per-user × per-report grant table.
//
// Added 2026-07-21 at user request. Shows exactly which users can
// access which online reports, whether granted via normal approval
// (per-slug via `approved_slugs`) or super-admin bypass (implicit
// via the signed `mr_stock_admin` cookie mechanism).
//
// Searchable by user name / email / phone, filterable by report
// type (kind = stock | outstanding | …). Polls every 30 s so a new
// approval shows up without a manual refresh.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Users, Search, RefreshCw, ShieldCheck, KeyRound, Package, Wallet, Filter } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { formatDateTime } from "../lib/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  stock:        { label: "Stock",       cls: "bg-blue-100 text-blue-800",       icon: Package },
  outstanding:  { label: "Outstanding", cls: "bg-purple-100 text-purple-800",   icon: Wallet },
};

const METHOD_META = {
  normal: { label: "Approved",           cls: "bg-emerald-100 text-emerald-800", icon: ShieldCheck },
  bypass: { label: "Super Admin Bypass", cls: "bg-amber-100 text-amber-800",     icon: KeyRound },
};

const fmtDt = (iso) => formatDateTime(iso) || "—";

export const UserAccessOverview = ({ authHeaders }) => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [reportFilter, setReportFilter] = useState("__all__");
  const [methodFilter, setMethodFilter] = useState("__all__");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/access-overview`, { headers: authHeaders });
      setRows(r.data?.rows || []);
      setReports(r.data?.reports || []);
    } catch (e) {
      toast({
        title: "Failed to load access overview",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [authHeaders, toast]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (reportFilter !== "__all__" && r.report_kind !== reportFilter) return false;
      if (methodFilter !== "__all__" && r.approval_method !== methodFilter) return false;
      if (!q) return true;
      return (r.user_name || "").toLowerCase().includes(q)
          || (r.user_phone || "").toLowerCase().includes(q)
          || (r.user_company || "").toLowerCase().includes(q)
          || (r.user_ip || "").toLowerCase().includes(q)
          || (r.report_label || "").toLowerCase().includes(q);
    });
  }, [rows, query, reportFilter, methodFilter]);

  const kindOptions = useMemo(() => {
    const kinds = new Set(rows.map(r => r.report_kind).filter(Boolean));
    return Array.from(kinds).sort();
  }, [rows]);

  // Group rows by user for a compact "user → reports they access" view.
  const groupedByUser = useMemo(() => {
    const groups = new Map();
    visible.forEach(r => {
      const key = r.user_id || `${r.user_name}|${r.user_phone}`;
      if (!groups.has(key)) {
        groups.set(key, {
          user_id: r.user_id,
          user_name: r.user_name || "(no name)",
          user_phone: r.user_phone,
          user_company: r.user_company,
          user_ip: r.user_ip,
          approval_method: r.approval_method,
          reports: [],
        });
      }
      groups.get(key).reports.push({
        slug: r.report_slug,
        kind: r.report_kind,
        label: r.report_label,
        granted_at: r.granted_at,
        last_seen_at: r.last_seen_at,
      });
    });
    return Array.from(groups.values());
  }, [visible]);

  return (
    <div className="space-y-4" data-testid="user-access-overview">
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#0078D4]" />
              <h2 className="text-lg font-bold text-neutral-900">User Access Overview</h2>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              Every user × report combination they can access, whether via normal approval or super admin bypass.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="overview-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Search + filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
          <div className="relative sm:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search name, phone, company, report…"
              className="pl-9 h-9"
              data-testid="overview-search"
            />
          </div>
          <Select value={reportFilter} onValueChange={setReportFilter}>
            <SelectTrigger className="h-9" data-testid="overview-filter-kind">
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-neutral-400" />
                <SelectValue placeholder="Report type" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All report types</SelectItem>
              {kindOptions.map(k => (
                <SelectItem key={k} value={k}>{KIND_META[k]?.label || k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={methodFilter} onValueChange={setMethodFilter}>
            <SelectTrigger className="h-9" data-testid="overview-filter-method">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-neutral-400" />
                <SelectValue placeholder="Approval method" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All approval methods</SelectItem>
              <SelectItem value="normal">Normal approval</SelectItem>
              <SelectItem value="bypass">Super Admin Bypass</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Summary strip */}
        <div className="flex gap-3 mt-4 text-xs text-neutral-500">
          <span data-testid="overview-count-users">
            <span className="font-bold text-neutral-900">{groupedByUser.length}</span> users
          </span>
          <span>·</span>
          <span data-testid="overview-count-grants">
            <span className="font-bold text-neutral-900">{visible.length}</span> grants
          </span>
          <span>·</span>
          <span data-testid="overview-count-reports">
            <span className="font-bold text-neutral-900">{reports.length}</span> reports in scope
          </span>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-center text-neutral-400 py-12" data-testid="overview-loading">Loading…</div>
      ) : groupedByUser.length === 0 ? (
        <div className="text-center text-neutral-400 py-12 bg-white border border-dashed border-neutral-200 rounded-xl" data-testid="overview-empty">
          No user grants match the current filters.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100 overflow-hidden">
          {groupedByUser.map((u, idx) => {
            const methodMeta = METHOD_META[u.approval_method] || METHOD_META.normal;
            const MethodIcon = methodMeta.icon;
            return (
              <div
                key={u.user_id || idx}
                className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:gap-4"
                data-testid={`overview-row-${u.user_id || idx}`}
              >
                {/* User block */}
                <div className="sm:w-64 sm:min-w-[240px] flex-shrink-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-neutral-900 truncate">{u.user_name}</span>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${methodMeta.cls}`}
                      data-testid={`overview-method-badge-${u.user_id || idx}`}
                    >
                      <MethodIcon className="w-3 h-3" />
                      {methodMeta.label}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-600 space-y-0.5">
                    {u.user_phone && <div>{u.user_phone}</div>}
                    {u.user_company && <div className="text-neutral-500 truncate">{u.user_company}</div>}
                    {u.user_ip && <div className="text-neutral-400 font-mono text-[11px]">{u.user_ip}</div>}
                  </div>
                </div>

                {/* Reports block */}
                <div className="flex-1 flex flex-wrap gap-1.5" data-testid={`overview-reports-${u.user_id || idx}`}>
                  {u.reports.map((rep, ri) => {
                    const kindMeta = KIND_META[rep.kind] || { label: rep.kind || "report", cls: "bg-neutral-100 text-neutral-800", icon: Package };
                    const KindIcon = kindMeta.icon;
                    return (
                      <span
                        key={`${rep.slug}-${ri}`}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${kindMeta.cls}`}
                        title={`Granted ${fmtDt(rep.granted_at)}`}
                        data-testid={`overview-report-${u.user_id || idx}-${rep.slug || ri}`}
                      >
                        <KindIcon className="w-3 h-3" />
                        <span>{rep.label}</span>
                      </span>
                    );
                  })}
                  {u.reports.length === 0 && (
                    <span className="text-xs text-neutral-400 italic">(no reports in scope)</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserAccessOverview;
